/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { FaEdit, FaFilePdf, FaTrash } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { useAuthContext } from "../../context/auth";
import { calcularLedgerComisionEdificio } from "../../utils/calcularLedgerComisionEdificio";
import {
  FECHA_CORTE,
  buildSaldoInicialEntry,
  construirLedgerCliente,
  construirLedgerProveedor,
  encontrarSaldoInicial,
  toDisplayCurrency as toDisplayCurrencyRaw,
} from "../../utils/estadoCuenta";
import { getPagosEmpleados } from "../../utils/pagosEmpleados";
import { getSaldosIniciales, upsertSaldoInicial } from "../../utils/saldosIniciales";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import FacturaExternaModal from "../FacturaExternaModal";
import { Header } from "../Header";
import styles from "./EstadoCuenta.module.scss";

// Helpers de periodo_mes — TZ-safe (no usar toISOString en husos negativos).
// Duplicados del modal de carga a propósito; FacturaExternaModal no debe modificarse en esta fase.
const MESES_LABELS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const MESES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const formatPeriodo = (periodoMes) => {
  if (!periodoMes) return null;
  const [y, m] = periodoMes.split("-");
  const idx = parseInt(m, 10) - 1;
  if (idx < 0 || idx > 11) return null;
  return `${MESES_CORTOS[idx]} ${y}`;
};

const defaultPeriodoMes = (fechaStr) => {
  if (!fechaStr) {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }
  const [y, m] = fechaStr.split("-").map(Number);
  return new Date(y, m - 1, 1);
};

const periodoMesToString = (date) => {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
};

const SUB_TABS = [
  { value: "proveedor", label: "Proveedores" },
  { value: "cliente", label: "Clientes" },
  { value: "empleado", label: "Empleados" },
  { value: "edificio", label: "Edificios" },
];

const VISTAS_EDIFICIO = [
  { value: "mensual", label: "Mensual" },
  { value: "trimestral", label: "Trimestral" },
  { value: "anual", label: "Anual" },
];

const TIPO_PAGO_LABEL = {
  sueldo: "Sueldo",
  adelanto: "Adelanto",
  comision: "Comisión",
  bono: "Bono",
  finiquito: "Finiquito",
  transporte: "Transporte",
};

const formatFechaDisplay = (fechaStr) => {
  if (!fechaStr) return "-";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [year, month, day] = f.split("-");
  return `${day}/${month}/${year}`;
};

const getTrimestreFromDate = (fechaStr) => {
  if (!fechaStr) return "";
  const month = parseInt(fechaStr.substring(5, 7), 10);
  const year = fechaStr.substring(0, 4);
  const q = Math.ceil(month / 3);
  return `${year} Q${q}`;
};

const agruparPorTrimestre = (entries) => {
  const grouped = {};
  entries.forEach((e) => {
    const key = e.trimestre || getTrimestreFromDate(e.fecha);
    if (!key) return;
    if (!grouped[key]) {
      grouped[key] = {
        fecha: e.fecha,
        trimestre: key,
        concepto: key,
        referencia: "-",
        debito: 0,
        credito: 0,
        moneda: e.moneda,
      };
    }
    grouped[key].debito += e.debito;
    grouped[key].credito += e.credito;
  });
  return Object.values(grouped).sort((a, b) => a.fecha.localeCompare(b.fecha));
};

const agruparPorAno = (entries) => {
  const grouped = {};
  entries.forEach((e) => {
    const year = e.fecha ? e.fecha.substring(0, 4) : "?";
    if (!grouped[year]) {
      grouped[year] = {
        fecha: `${year}-12-31`,
        concepto: year,
        referencia: "-",
        debito: 0,
        credito: 0,
        moneda: e.moneda,
      };
    }
    grouped[year].debito += e.debito;
    grouped[year].credito += e.credito;
  });
  return Object.values(grouped).sort((a, b) => a.fecha.localeCompare(b.fecha));
};

// Display de la columna Concepto, reglas estrictas:
//   1. Factura (origen SII/EXT/LEG): "Factura {ref}" o "Factura externa {ref}".
//   2. Descripción del movimiento: se muestra tal cual, sin prefijo.
//   3. Solo referencia.
//   4. Vacío.
// Nunca se prefija con "Pago", "Pago a cuenta", "Cobro" u otros conceptos genéricos.
const renderConcepto = (entry) => {
  const ref = entry.referencia && entry.referencia !== "-" ? entry.referencia : null;
  if (entry.origen === "SII") return ref ? `Factura ${ref}` : "Factura";
  if (entry.origen === "LEG") return ref ? `Factura ${ref}` : "Factura";
  if (entry.origen === "EXT") return entry.concepto || (ref ? `Factura externa ${ref}` : "Factura externa");
  if (entry.descripcion) return entry.descripcion;
  return ref || "";
};

const EstadoCuenta = () => {
  const { pais } = useAuthContext();
  const { formatAmount } = useCurrency();

  const normalizedPais = pais ? pais.charAt(0).toUpperCase() + pais.slice(1).toLowerCase() : "";
  const monedaPrincipal = normalizedPais === "Chile" ? "CLP" : "PEN";

  const [subTab, setSubTab] = useState("proveedor");
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [monedaSaldo, setMonedaSaldo] = useState(monedaPrincipal);
  const [vistaEdificio, setVistaEdificio] = useState("mensual");
  const [searchParams] = useSearchParams();

  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [movimientosCaja, setMovimientosCaja] = useState([]);
  const [siiRecibidas, setSiiRecibidas] = useState([]);
  const [siiEmitidas, setSiiEmitidas] = useState([]);
  const [facturasExternas, setFacturasExternas] = useState([]);
  const [facturasProveedor, setFacturasProveedor] = useState([]);
  const [pagosEmpleados, setPagosEmpleados] = useState([]);
  const [comisionesEdificio, setComisionesEdificio] = useState([]);
  const [loadingComisiones, setLoadingComisiones] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saldosIniciales, setSaldosIniciales] = useState([]);
  const [editandoSaldo, setEditandoSaldo] = useState(false);
  const [mostrarFacturaModal, setMostrarFacturaModal] = useState(false);
  const [editandoFacturaExt, setEditandoFacturaExt] = useState(null);

  // ─── Carga inicial de datos ───
  useEffect(() => {
    if (!normalizedPais) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      client.from("proveedores").select("id, nombre, moneda_recurrente").eq("pais", normalizedPais).order("nombre"),
      client.from("clientes").select("id, nombre, empresa").eq("pais", normalizedPais),
      client
        .from("empleados")
        .select("id, nombre, apellido, pais, egreso_fecha")
        .eq("pais", normalizedPais)
        .order("nombre"),
      client.from("edificios").select("id, edificio, alianza, pais").eq("pais", normalizedPais),
      client
        .from("movimientos_caja")
        .select(
          "id, fecha, monto, moneda, tipo, entidad_tipo, entidad_id, descripcion, numero_documento, categoria_pago, estado"
        )
        .eq("pais", normalizedPais)
        .neq("estado", "anulado")
        .gte("fecha", FECHA_CORTE)
        .order("fecha", { ascending: false }),
      client
        .from("facturas_sii_recibidas")
        .select("id, proveedor_id, fecha_emision, monto_total, folio, tipo_dte")
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      client
        .from("facturas_sii_emitidas")
        .select("id, cliente_id, fecha_emision, monto_total, folio, tipo_dte")
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      client
        .from("facturas_externas")
        .select(
          "id, proveedor_id, fecha_emision, monto_total, moneda, numero_documento, estado, concepto, archivo_url, periodo_mes"
        )
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      client
        .from("facturas_proveedor")
        .select("id, proveedor_id, fecha_emision, monto_total, moneda, numero_factura, estado")
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      getPagosEmpleados({ pais: normalizedPais, desde: FECHA_CORTE }),
      getSaldosIniciales(normalizedPais),
    ])
      .then(([provs, clis, emps, edifs, movs, sR, sE, fE, fP, pE, sI]) => {
        if (cancelled) return;
        setProveedores(provs.data || []);
        setClientes(clis.data || []);
        setEmpleados(emps.data || []);
        setEdificios(edifs.data || []);
        setMovimientosCaja(movs.data || []);
        setSiiRecibidas(sR.data || []);
        setSiiEmitidas(sE.data || []);
        setFacturasExternas(fE.data || []);
        setFacturasProveedor(fP.data || []);
        setPagosEmpleados(pE || []);
        setSaldosIniciales(sI || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error cargando estado de cuenta:", err);
        toast.error("Error cargando estado de cuenta");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [normalizedPais]);

  // ─── Preselección via URL params (?tipo=proveedor&id=X) ───
  useEffect(() => {
    const tipo = searchParams.get("tipo");
    const id = searchParams.get("id");
    if (!tipo || !id) return;
    if (tipo === "proveedor" && proveedores.length > 0) {
      setSubTab("proveedor");
      setSelectedEntityId(Number(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveedores]);

  // ─── Auto-detectar moneda predominante al elegir proveedor ───
  // Si hay facturas cargadas → usar la moneda mayoritaria. Si no hay facturas todavía
  // → fallback a proveedor.moneda_recurrente (configurada en GestionProveedores). Último
  // recurso: monedaPrincipal del país.
  useEffect(() => {
    if (subTab !== "proveedor" || !selectedEntityId) return;
    const provId = Number(selectedEntityId);
    const fxs = facturasExternas.filter((f) => f.proveedor_id === provId && f.estado !== "anulada");
    const fps = facturasProveedor.filter((f) => f.proveedor_id === provId && f.estado !== "anulada");
    const total = fxs.length + fps.length;
    if (total === 0) {
      const proveedor = proveedores.find((p) => p.id === provId);
      setMonedaSaldo(proveedor?.moneda_recurrente || monedaPrincipal);
      return;
    }
    const usdCount = [...fxs, ...fps].filter((f) => f.moneda === "USD").length;
    setMonedaSaldo(usdCount > total / 2 ? "USD" : monedaPrincipal);
  }, [selectedEntityId, subTab, facturasExternas, facturasProveedor, monedaPrincipal, proveedores]);

  // ─── Resolver IDs de edificios según selección ───
  const resolverEdificioIds = useCallback(
    (entityId) => {
      if (!entityId) return [];
      if (entityId === "todos") return edificios.map((e) => e.id);
      if (entityId.startsWith("alianza:")) {
        const alianza = entityId.replace("alianza:", "");
        return edificios.filter((e) => e.alianza === alianza).map((e) => e.id);
      }
      return [Number(entityId)];
    },
    [edificios]
  );

  // ─── Lazy-load comisiones edificio ───
  useEffect(() => {
    if (subTab !== "edificio" || !selectedEntityId) {
      setComisionesEdificio([]);
      return;
    }
    let cancelled = false;
    const fetchComisiones = async () => {
      setLoadingComisiones(true);
      try {
        const ids = resolverEdificioIds(selectedEntityId);
        if (ids.length === 0) {
          if (!cancelled) setComisionesEdificio([]);
          return;
        }
        const edificiosMap = {};
        edificios.forEach((e) => {
          edificiosMap[e.id] = e;
        });
        const result = await calcularLedgerComisionEdificio({
          edificioIds: ids,
          edificiosMap,
          fechaInicio: FECHA_CORTE,
          fechaFin: `${new Date().getFullYear() + 1}-12-31`,
          pais: normalizedPais,
        });
        if (!cancelled) setComisionesEdificio(result);
      } catch (err) {
        console.error("Error calculando comisiones edificio:", err);
        if (!cancelled) setComisionesEdificio([]);
      } finally {
        if (!cancelled) setLoadingComisiones(false);
      }
    };
    fetchComisiones();
    return () => {
      cancelled = true;
    };
  }, [subTab, selectedEntityId, edificios, normalizedPais, resolverEdificioIds]);

  // ─── Helper de conversión moneda ───
  // Wrapper que cierra sobre {monedaSaldo, monedaPrincipal, pais} para que las
  // llamadas en los ledgers se mantengan en forma de 3 args (monto, moneda, fecha).
  const toDisplayCurrency = useCallback(
    (monto, monedaOrigen, fecha) =>
      toDisplayCurrencyRaw(monto, monedaOrigen, fecha, { monedaSaldo, monedaPrincipal, pais: normalizedPais }),
    [monedaSaldo, monedaPrincipal, normalizedPais]
  );

  // ─── Opciones del dropdown ───
  const uniqueAlianzas = useMemo(
    () => [...new Set(edificios.map((e) => e.alianza).filter(Boolean))].sort(),
    [edificios]
  );

  const entityOptions = useMemo(() => {
    if (subTab === "proveedor") return proveedores.map((p) => ({ value: p.id, label: p.nombre }));
    if (subTab === "cliente") {
      return [...clientes]
        .sort((a, b) => (a.empresa || a.nombre || "").localeCompare(b.empresa || b.nombre || ""))
        .map((c) => ({ value: c.id, label: c.empresa || c.nombre }));
    }
    if (subTab === "empleado") {
      return empleados.filter((e) => !e.egreso_fecha).map((e) => ({ value: e.id, label: `${e.nombre} ${e.apellido}` }));
    }
    return [];
  }, [subTab, proveedores, clientes, empleados]);

  const entityLabel =
    subTab === "proveedor"
      ? "Proveedor"
      : subTab === "cliente"
        ? "Cliente"
        : subTab === "empleado"
          ? "Empleado"
          : "Edificio";

  // ─── Saldo inicial de la entidad activa (si aplica) ───
  // Solo se busca para entidades simples (no para "todos" ni alianzas en edificios).
  const saldoInicialActivo = useMemo(
    () => encontrarSaldoInicial(saldosIniciales, subTab, selectedEntityId),
    [saldosIniciales, subTab, selectedEntityId]
  );

  // ─── LEDGER: PROVEEDORES (3 fuentes + pagos) ───
  const ledgerProveedor = useMemo(() => {
    if (subTab !== "proveedor" || !selectedEntityId) return [];
    return construirLedgerProveedor({
      provId: Number(selectedEntityId),
      siiRecibidas,
      facturasExternas,
      facturasProveedor,
      movimientosCaja,
      saldoInicial: saldoInicialActivo,
      monedaPrincipal,
      toDisplayCurrency,
    }).entries;
  }, [
    subTab,
    selectedEntityId,
    siiRecibidas,
    facturasExternas,
    facturasProveedor,
    movimientosCaja,
    monedaPrincipal,
    toDisplayCurrency,
    saldoInicialActivo,
  ]);

  // ─── LEDGER: CLIENTES (SII emitidas + cobros) ───
  const ledgerCliente = useMemo(() => {
    if (subTab !== "cliente" || !selectedEntityId) return [];
    return construirLedgerCliente({
      cliId: Number(selectedEntityId),
      siiEmitidas,
      movimientosCaja,
      saldoInicial: saldoInicialActivo,
      monedaPrincipal,
      toDisplayCurrency,
    });
  }, [subTab, selectedEntityId, siiEmitidas, movimientosCaja, monedaPrincipal, toDisplayCurrency, saldoInicialActivo]);

  // ─── LEDGER: EMPLEADOS (pagos_empleados) ───
  const ledgerEmpleado = useMemo(() => {
    if (subTab !== "empleado" || !selectedEntityId) return [];
    const empId = Number(selectedEntityId);

    const pagos = pagosEmpleados
      .filter((p) => p.empleado_id === empId && (p.fecha_pago || "") >= FECHA_CORTE)
      .map((p) => ({
        fecha: p.fecha_pago,
        concepto: TIPO_PAGO_LABEL[p.tipo] || p.tipo || "Pago",
        referencia: p.periodo_año && p.periodo_mes ? `${String(p.periodo_mes).padStart(2, "0")}/${p.periodo_año}` : "-",
        debito: 0,
        credito: Number(p.monto || 0),
        moneda: p.moneda || monedaPrincipal,
      }));

    const inicial = saldoInicialActivo ? [buildSaldoInicialEntry(saldoInicialActivo, monedaPrincipal)] : [];
    const entries = [...inicial, ...pagos].sort((a, b) => a.fecha.localeCompare(b.fecha));

    let saldo = 0;
    return entries
      .map((entry) => {
        const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
        const creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
        saldo += debitoDisplay - creditoDisplay;
        return { ...entry, debitoDisplay, creditoDisplay, saldo };
      })
      .reverse();
  }, [subTab, selectedEntityId, pagosEmpleados, monedaPrincipal, toDisplayCurrency, saldoInicialActivo]);

  // ─── LEDGER: EDIFICIOS (comisiones + pagos) ───
  const ledgerEdificio = useMemo(() => {
    if (subTab !== "edificio" || !selectedEntityId) return [];

    const targetIds = new Set(resolverEdificioIds(selectedEntityId));

    const debitos = comisionesEdificio.map((c) => ({
      fecha: c.fecha,
      trimestre: c.trimestre,
      concepto: c.concepto,
      referencia: "-",
      debito: c.debito,
      credito: 0,
      moneda: c.moneda,
    }));

    const creditos = movimientosCaja
      .filter((m) => m.tipo === "pago" && m.entidad_tipo === "edificio" && targetIds.has(m.entidad_id))
      .map((m) => ({
        fecha: m.fecha,
        trimestre: getTrimestreFromDate(m.fecha),
        concepto: "Pago",
        referencia: m.numero_documento || "-",
        debito: 0,
        credito: Number(m.monto),
        moneda: m.moneda || monedaPrincipal,
      }));

    // Saldo inicial solo aplica cuando es un edificio individual (no "todos" ni alianza)
    const inicialEntry = saldoInicialActivo
      ? [
          {
            ...buildSaldoInicialEntry(saldoInicialActivo, monedaPrincipal),
            trimestre: getTrimestreFromDate(FECHA_CORTE),
          },
        ]
      : [];
    let entries = [...inicialEntry, ...debitos, ...creditos].sort((a, b) => a.fecha.localeCompare(b.fecha));

    if (vistaEdificio === "trimestral") entries = agruparPorTrimestre(entries);
    else if (vistaEdificio === "anual") entries = agruparPorAno(entries);

    let saldo = 0;
    return entries
      .map((entry) => {
        const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
        const creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
        saldo += debitoDisplay - creditoDisplay;
        return { ...entry, debitoDisplay, creditoDisplay, saldo };
      })
      .reverse();
  }, [
    subTab,
    selectedEntityId,
    comisionesEdificio,
    movimientosCaja,
    vistaEdificio,
    monedaPrincipal,
    toDisplayCurrency,
    resolverEdificioIds,
    saldoInicialActivo,
  ]);

  const ledger =
    subTab === "proveedor"
      ? ledgerProveedor
      : subTab === "cliente"
        ? ledgerCliente
        : subTab === "empleado"
          ? ledgerEmpleado
          : ledgerEdificio;

  const totalFacturado = ledger
    .filter((e) => !e.bucket || e.bucket === "factura")
    .reduce((s, e) => s + e.debitoDisplay, 0);
  const totalPagado = ledger
    .filter((e) => !e.bucket || e.bucket === "factura")
    .reduce((s, e) => s + e.creditoDisplay, 0);
  const totalDebeCredito = ledger.filter((e) => e.bucket === "credito").reduce((s, e) => s + e.debitoDisplay, 0);
  const totalHaberCredito = ledger.filter((e) => e.bucket === "credito").reduce((s, e) => s + e.creditoDisplay, 0);
  const creditoNeto = totalDebeCredito - totalHaberCredito;
  const saldoFinal = totalFacturado - totalPagado + creditoNeto;

  // El botón "Editar saldo inicial" solo tiene sentido para entidades simples (no agregaciones).
  const puedeEditarSaldoInicial = !!selectedEntityId && Number.isFinite(Number(selectedEntityId));

  const handleGuardarSaldoInicial = async ({ monto, moneda, notas }) => {
    if (!puedeEditarSaldoInicial) return;
    try {
      const saved = await upsertSaldoInicial({
        pais: normalizedPais,
        entidad_tipo: subTab,
        entidad_id: Number(selectedEntityId),
        monto,
        moneda,
        notas,
      });
      setSaldosIniciales((prev) => {
        const idx = prev.findIndex((s) => s.id === saved.id);
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = saved;
          return copy;
        }
        return [...prev, saved];
      });
      toast.success("Saldo inicial guardado");
      setEditandoSaldo(false);
    } catch (err) {
      console.error("Error guardando saldo inicial:", err);
      toast.error("Error guardando saldo inicial");
    }
  };

  const handleDeleteFacturaExt = async (entry) => {
    const labelEntidad = entry.concepto || entry.referencia || "esta factura";
    if (!window.confirm(`¿Eliminar "${labelEntidad}"? Esta acción no se puede deshacer.`)) return;
    try {
      // Limpiar refs en movimientos_bancarios antes del DELETE para evitar match_factura_id
      // huérfanas (FK polimórfica sin ON DELETE CASCADE). Fallo acá se loguea pero no aborta:
      // preferimos completar la eliminación aunque queden refs huérfanas que limpiar después.
      const { error: refsErr } = await client
        .from("movimientos_bancarios")
        .update({ match_factura_tipo: null, match_factura_id: null })
        .eq("match_factura_tipo", "factura_externa")
        .eq("match_factura_id", entry.facturaId);
      if (refsErr) console.error("Error limpiando refs en movimientos_bancarios:", refsErr);

      // Eliminar movimientos_caja generados por esta factura (vía flujo bancario).
      // Match: mismo movimiento_bancario_id + mismo proveedor. El movimiento bancario
      // queda intacto (lo desvinculamos en el paso anterior). Si no hay
      // movimiento_bancario_id en la factura, la factura fue cargada manual y no hay
      // mov_caja asociado por esta cadena, así que se saltea.
      const facturaDb = facturasExternas.find((f) => f.id === entry.facturaId);
      const movBancarioId = facturaDb?.movimiento_bancario_id;
      const proveedorId = facturaDb?.proveedor_id;
      if (movBancarioId && proveedorId) {
        const { data: cajasEliminadas, error: cajaErr } = await client
          .from("movimientos_caja")
          .delete()
          .eq("movimiento_bancario_id", movBancarioId)
          .eq("entidad_tipo", "proveedor")
          .eq("entidad_id", proveedorId)
          .select("id");
        if (cajaErr) {
          console.error("Error eliminando movimientos_caja vinculados:", cajaErr);
        } else if (cajasEliminadas?.length) {
          const eliminadosIds = new Set(cajasEliminadas.map((m) => m.id));
          setMovimientosCaja((prev) => prev.filter((m) => !eliminadosIds.has(m.id)));

          // Nulear FK huérfana en movimientos_bancarios y devolverlo a pendiente. Sin
          // esto, el banco queda con movimiento_caja_id apuntando a un row borrado y
          // FlujoCajaV2 cae al fallback "badge G" porque el embed devuelve null.
          // Fallo no aborta el flujo de eliminación.
          const { error: bancoErr } = await client
            .from("movimientos_bancarios")
            .update({ movimiento_caja_id: null, estado: "pendiente" })
            .eq("id", movBancarioId);
          if (bancoErr) console.error("Error nuleando movimiento_caja_id en banco:", bancoErr);
        }
      }

      const { error } = await client.from("facturas_externas").delete().eq("id", entry.facturaId);
      if (error) throw error;
      setFacturasExternas((prev) => prev.filter((f) => f.id !== entry.facturaId));
      toast.success("Factura eliminada");
    } catch (err) {
      console.error("Error eliminando factura externa:", err);
      toast.error("Error eliminando factura: " + (err.message || ""));
    }
  };

  const handleFacturaExtEditada = (updated) => {
    setFacturasExternas((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
    setEditandoFacturaExt(null);
  };

  // ─── RENDER ───
  return (
    <div className={styles.wrapper}>
      <Header
        leftContent={
          <div style={{ display: "flex", alignItems: "center", marginLeft: 150 }}>
            <div
              style={{
                display: "flex",
                gap: 0,
                borderRadius: 6,
                overflow: "hidden",
                border: "1px solid #dee2e6",
              }}
            >
              {SUB_TABS.map((st) => (
                <button
                  key={st.value}
                  type="button"
                  onClick={() => {
                    setSubTab(st.value);
                    setSelectedEntityId("");
                    setMonedaSaldo(monedaPrincipal);
                  }}
                  style={{
                    height: 32,
                    padding: "0 18px",
                    fontSize: "0.82rem",
                    fontWeight: 400,
                    border: "none",
                    cursor: "pointer",
                    background: subTab === st.value ? "#2c3e50" : "transparent",
                    color: subTab === st.value ? "#fff" : "#666",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {st.label}
                </button>
              ))}
            </div>
          </div>
        }
      >
        {/* Botones a la izquierda del selector — el selector va último para que quede anclado
            a la derecha del rightGroup (que es flex con margin-right:2rem). */}
        {!loading && puedeEditarSaldoInicial && (
          <button type="button" className={styles.btnSaldoInicial} onClick={() => setEditandoSaldo(true)}>
            {saldoInicialActivo ? "Editar saldo inicial" : "+ Saldo inicial"}
          </button>
        )}
        {!loading && subTab === "proveedor" && selectedEntityId && (
          <button type="button" className={styles.btnSaldoInicial} onClick={() => setMostrarFacturaModal(true)}>
            + Cargar factura
          </button>
        )}
        {!loading && selectedEntityId && subTab !== "edificio" && (
          <select
            className={`form-select ${styles.monedaSelect}`}
            value={monedaSaldo}
            onChange={(e) => setMonedaSaldo(e.target.value)}
          >
            <option value={monedaPrincipal}>{monedaPrincipal}</option>
            <option value="USD">USD</option>
          </select>
        )}
        {!loading && subTab === "edificio" && (
          <select
            className={`form-select ${styles.entitySelect}`}
            value={selectedEntityId}
            onChange={(e) => setSelectedEntityId(e.target.value)}
          >
            <option value="">Seleccionar edificio...</option>
            {edificios.length > 1 && <option value="todos">Todos</option>}
            {uniqueAlianzas.length > 0 && (
              <optgroup label="Alianzas">
                {uniqueAlianzas.map((a) => (
                  <option key={`alianza:${a}`} value={`alianza:${a}`}>
                    {a}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Edificios">
              {[...edificios]
                .sort((a, b) => (a.edificio || "").localeCompare(b.edificio || ""))
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.edificio}
                  </option>
                ))}
            </optgroup>
          </select>
        )}
        {!loading && subTab !== "edificio" && (
          <select
            className={`form-select ${styles.entitySelect}`}
            value={selectedEntityId}
            onChange={(e) => setSelectedEntityId(e.target.value)}
          >
            <option value="">Seleccionar {entityLabel.toLowerCase()}...</option>
            {entityOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
      </Header>
      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>Cargando…</div>
        ) : (
          <>
            {!selectedEntityId && (
              <div className={styles.empty}>Seleccione una entidad para ver su estado de cuenta</div>
            )}
            {selectedEntityId && loadingComisiones && subTab === "edificio" && (
              <div className={styles.empty}>Calculando comisiones...</div>
            )}
            {selectedEntityId && !loadingComisiones && ledger.length === 0 && (
              <div className={styles.empty}>Sin movimientos registrados desde 2026-01-01</div>
            )}

            {selectedEntityId && !loadingComisiones && ledger.length > 0 && (
              <>
                <SaldoResumen
                  subTab={subTab}
                  totalFacturado={totalFacturado}
                  totalPagado={totalPagado}
                  creditoNeto={creditoNeto}
                  saldoFinal={saldoFinal}
                  monedaSaldo={monedaSaldo}
                  formatAmount={formatAmount}
                />

                {subTab === "edificio" && (
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                    <div className={styles.vistaToggle}>
                      {VISTAS_EDIFICIO.map((v) => (
                        <button
                          key={v.value}
                          className={`${styles.vistaOption} ${vistaEdificio === v.value ? styles.vistaActiva : ""}`}
                          onClick={() => setVistaEdificio(v.value)}
                        >
                          {v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className={styles.tableWrapper}>
                  <LedgerTable
                    subTab={subTab}
                    ledger={ledger}
                    monedaSaldo={monedaSaldo}
                    formatAmount={formatAmount}
                    onEditFacturaExt={setEditandoFacturaExt}
                    onDeleteFacturaExt={handleDeleteFacturaExt}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      {editandoSaldo && puedeEditarSaldoInicial && (
        <SaldoInicialModal
          tipoLabel={entityLabel}
          saldoActual={saldoInicialActivo}
          monedaPrincipal={monedaPrincipal}
          onClose={() => setEditandoSaldo(false)}
          onGuardar={handleGuardarSaldoInicial}
        />
      )}

      {mostrarFacturaModal && subTab === "proveedor" && selectedEntityId && (
        <FacturaExternaModal
          show={mostrarFacturaModal}
          onClose={() => setMostrarFacturaModal(false)}
          pais={normalizedPais}
          moneda={monedaSaldo}
          proveedorId={Number(selectedEntityId)}
          proveedores={proveedores}
          onFacturaCreada={(fe) => setFacturasExternas((prev) => [...prev, fe])}
        />
      )}

      {editandoFacturaExt && (
        <EditarFacturaExternaModal
          entry={editandoFacturaExt}
          onClose={() => setEditandoFacturaExt(null)}
          onSaved={handleFacturaExtEditada}
        />
      )}
    </div>
  );
};

const SaldoResumen = ({ subTab, totalFacturado, totalPagado, creditoNeto, saldoFinal, monedaSaldo, formatAmount }) => {
  const labelsByTab = {
    proveedor: ["Total facturado", "Total pagado", "Crédito", "Saldo"],
    cliente: ["Total facturado", "Total cobrado", "Crédito", "Saldo"],
    empleado: ["Debe", "Total pagado", "Crédito", "Saldo"],
    edificio: ["Comisión generada", "Total pagado", "Crédito", "Saldo"],
  };
  const [labelDebe, labelHaber, labelCredito, labelSaldo] = labelsByTab[subTab] || [
    "Debe",
    "Haber",
    "Crédito",
    "Saldo",
  ];

  // > 0: deudor (rojo borgoña). < 0: a favor (verde). Cero: neutral. Misma regla para Saldo y Crédito.
  const saldoBg = saldoFinal > 0 ? "#722f37" : saldoFinal < 0 ? "#198754" : "#343a40";
  const creditoBg = creditoNeto > 0 ? "#722f37" : creditoNeto < 0 ? "#198754" : "#343a40";

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 16,
    width: "75%",
    margin: "16px auto",
    padding: "14px 0",
  };
  const cardStyle = {
    borderRadius: 6,
    padding: "12px 24px",
    textAlign: "center",
    color: "#fff",
  };
  const labelStyle = {
    fontSize: "0.75rem",
    fontWeight: 500,
    marginBottom: 5,
    paddingBottom: 5,
    borderBottom: "1px solid rgba(255,255,255,0.3)",
    opacity: 0.9,
    textTransform: "uppercase",
  };
  const valueStyle = { fontSize: "1.05rem", fontWeight: 700, marginTop: 5 };

  const cards = [
    { label: labelDebe, valor: totalFacturado, bg: "#343a40" },
    { label: labelHaber, valor: totalPagado, bg: "#343a40" },
    { label: labelCredito, valor: Math.abs(creditoNeto), bg: creditoBg },
    { label: labelSaldo, valor: Math.abs(saldoFinal), bg: saldoBg },
  ];

  return (
    <div style={gridStyle}>
      {cards.map((kpi) => (
        <div key={kpi.label} style={{ ...cardStyle, background: kpi.bg }}>
          <div style={labelStyle}>{kpi.label}</div>
          <div style={valueStyle}>{formatAmount(kpi.valor, monedaSaldo)}</div>
        </div>
      ))}
    </div>
  );
};

const LedgerTable = ({ subTab, ledger, monedaSaldo, formatAmount, onEditFacturaExt, onDeleteFacturaExt }) => {
  const isEdificio = subTab === "edificio";
  const showFolio = subTab !== "edificio" && subTab !== "empleado";
  const showAcciones = subTab === "proveedor";

  // Mezcla de monedas: si hay entries con moneda distinta a la del saldo, se separan
  // las columnas Debe/Haber por moneda. Cada entry aparece una sola vez en la columna
  // de su moneda nativa (sin conversión). El Saldo siempre va en monedaSaldo.
  const hayMezclaMonedas = !isEdificio && ledger.some((e) => e.moneda && e.moneda !== monedaSaldo);
  const monedaSecundaria = hayMezclaMonedas ? ledger.find((e) => e.moneda && e.moneda !== monedaSaldo)?.moneda : null;

  const tableClass = [
    styles.estadoCuentaTable,
    isEdificio ? styles.estadoCuentaEdificio : "",
    hayMezclaMonedas ? styles.estadoCuentaMezcla : "",
    showFolio ? styles.estadoCuentaConFolio : "",
    showAcciones ? styles.estadoCuentaConAcciones : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <table className={tableClass}>
      <thead>
        <tr>
          {isEdificio ? (
            <th>Período</th>
          ) : (
            <>
              <th>Fecha</th>
              <th>Concepto</th>
              {showFolio && <th>Folio</th>}
              {showAcciones && <th>Período</th>}
            </>
          )}
          {hayMezclaMonedas ? (
            <>
              <th>Debe {monedaSecundaria}</th>
              <th>Haber {monedaSecundaria}</th>
              <th>Debe {monedaSaldo}</th>
              <th>Haber {monedaSaldo}</th>
            </>
          ) : (
            <>
              <th>Debe</th>
              <th>Haber</th>
            </>
          )}
          <th>Saldo {monedaSaldo}</th>
          {showAcciones && <th>Acciones</th>}
        </tr>
      </thead>
      <tbody>
        {ledger.map((entry, idx) => {
          const monedaEntry = entry.moneda || monedaSaldo;
          const enSecundaria = hayMezclaMonedas && monedaEntry === monedaSecundaria;
          const enPrincipal = hayMezclaMonedas && monedaEntry === monedaSaldo;

          return (
            <tr key={idx}>
              {isEdificio ? (
                <td>{entry.concepto}</td>
              ) : (
                <>
                  <td>{formatFechaDisplay(entry.fecha)}</td>
                  <td>{renderConcepto(entry)}</td>
                  {showFolio && (
                    <td>
                      {["SII", "EXT", "LEG"].includes(entry.origen) && entry.referencia && entry.referencia !== "-" ? (
                        String(entry.referencia)
                      ) : (
                        <span style={{ color: "#999" }}>—</span>
                      )}
                    </td>
                  )}
                  {showAcciones && (
                    <td>{formatPeriodo(entry.periodo_mes) || <span style={{ color: "#999" }}>—</span>}</td>
                  )}
                </>
              )}
              {hayMezclaMonedas ? (
                <>
                  <td style={{ color: enSecundaria && entry.debito > 0 ? "#c62828" : undefined }}>
                    {enSecundaria && entry.debito > 0 ? formatAmount(entry.debito, monedaSecundaria) : ""}
                  </td>
                  <td style={{ color: enSecundaria && entry.credito > 0 ? "#2e7d32" : undefined }}>
                    {enSecundaria && entry.credito > 0 ? formatAmount(entry.credito, monedaSecundaria) : ""}
                  </td>
                  <td style={{ color: enPrincipal && entry.debito > 0 ? "#c62828" : undefined }}>
                    {enPrincipal && entry.debito > 0 ? formatAmount(entry.debito, monedaSaldo) : ""}
                  </td>
                  <td style={{ color: enPrincipal && entry.credito > 0 ? "#2e7d32" : undefined }}>
                    {enPrincipal && entry.credito > 0 ? formatAmount(entry.credito, monedaSaldo) : ""}
                  </td>
                </>
              ) : (
                <>
                  <td style={{ color: entry.debito > 0 ? "#c62828" : undefined }}>
                    {entry.debito > 0 ? formatAmount(entry.debito, monedaEntry) : ""}
                  </td>
                  <td style={{ color: entry.credito > 0 ? "#2e7d32" : undefined }}>
                    {entry.credito > 0 ? formatAmount(entry.credito, monedaEntry) : ""}
                  </td>
                </>
              )}
              <td
                style={{
                  fontWeight: 600,
                  color: entry.saldo > 0 ? "#c62828" : entry.saldo < 0 ? "#2e7d32" : "#333",
                }}
              >
                {formatAmount(Math.abs(entry.saldo), monedaSaldo)}
              </td>
              {showAcciones && (
                <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                  {entry.origen === "EXT" ? (
                    <>
                      <FaEdit
                        style={{ cursor: "pointer", fontSize: 14, color: "#495057", marginRight: 10 }}
                        title="Editar"
                        onClick={() => onEditFacturaExt?.(entry)}
                      />
                      <FaTrash
                        style={{ cursor: "pointer", fontSize: 14, color: "#495057", marginRight: 10 }}
                        title="Eliminar"
                        onClick={() => onDeleteFacturaExt?.(entry)}
                      />
                      {entry.archivo_url && (
                        <FaFilePdf
                          style={{ cursor: "pointer", fontSize: 14, color: "#495057" }}
                          title="Ver documento"
                          onClick={() => window.open(entry.archivo_url, "_blank", "noopener,noreferrer")}
                        />
                      )}
                    </>
                  ) : null}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const SaldoInicialModal = ({ tipoLabel, saldoActual, monedaPrincipal, onClose, onGuardar }) => {
  const [monto, setMonto] = useState(saldoActual ? String(saldoActual.monto) : "");
  const [moneda, setMoneda] = useState(saldoActual?.moneda || monedaPrincipal);
  const [notas, setNotas] = useState(saldoActual?.notas || "");
  const [guardando, setGuardando] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const montoNum = Number(monto);
    if (Number.isNaN(montoNum)) {
      toast.error("Monto inválido");
      return;
    }
    setGuardando(true);
    try {
      await onGuardar({ monto: montoNum, moneda, notas: notas.trim() });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          background: "#fff",
          borderRadius: 8,
          width: "min(440px, 95vw)",
          padding: 20,
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h5 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#1a3a5c" }}>
            Saldo inicial al 01/01/2026 — {tipoLabel}
          </h5>
          <button
            type="button"
            onClick={onClose}
            style={{ border: "none", background: "transparent", fontSize: "1.4rem", cursor: "pointer", color: "#666" }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div style={{ fontSize: "0.78rem", color: "#666", marginBottom: 12 }}>
          Positivo = la entidad nos debe. Negativo = le debemos a la entidad.
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: "0.78rem", color: "#555", marginBottom: 4 }}>Monto</label>
          <input
            type="number"
            step="any"
            className="form-control form-control-sm"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            autoFocus
            required
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: "0.78rem", color: "#555", marginBottom: 4 }}>Moneda</label>
          <select className="form-select form-select-sm" value={moneda} onChange={(e) => setMoneda(e.target.value)}>
            <option value={monedaPrincipal}>{monedaPrincipal}</option>
            <option value="USD">USD</option>
            {monedaPrincipal !== "CLP" && <option value="CLP">CLP</option>}
            {monedaPrincipal !== "PEN" && <option value="PEN">PEN</option>}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: "0.78rem", color: "#555", marginBottom: 4 }}>
            Notas (opcional)
          </label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Ej: cierre 2025, pendiente de cobro Empresa X"
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={guardando}>
            {guardando ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
};

const EditarFacturaExternaModal = ({ entry, onClose, onSaved }) => {
  const refInicial = entry.referencia && entry.referencia !== "-" ? entry.referencia : "";
  const [fecha, setFecha] = useState(entry.fecha);
  const [periodoMes, setPeriodoMes] = useState(() =>
    entry.periodo_mes ? defaultPeriodoMes(entry.periodo_mes) : defaultPeriodoMes(entry.fecha)
  );
  const [numeroDoc, setNumeroDoc] = useState(refInicial);
  const [concepto, setConcepto] = useState(entry.concepto || "");
  const [monto, setMonto] = useState(String(entry.debito ?? ""));
  const [saving, setSaving] = useState(false);

  const handleGuardar = async () => {
    const montoNum = Number(monto || 0);
    if (!fecha) return toast.error("Ingresá la fecha del documento");
    if (!concepto.trim()) return toast.error("Ingresá el concepto");
    if (montoNum <= 0) return toast.error("Ingresá el monto");
    setSaving(true);
    try {
      const { data, error } = await client
        .from("facturas_externas")
        .update({
          fecha_emision: fecha,
          periodo_mes: periodoMesToString(periodoMes),
          numero_documento: numeroDoc.trim() || null,
          concepto: concepto.trim(),
          monto_neto: montoNum,
          monto_total: montoNum,
        })
        .eq("id", entry.facturaId)
        .select()
        .single();
      if (error) throw error;
      toast.success("Factura actualizada");
      onSaved(data);
    } catch (err) {
      console.error("Error actualizando factura externa:", err);
      toast.error("Error al guardar: " + (err.message || ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Editar factura externa</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Fecha documento</div>
          <input
            type="date"
            className="form-control form-control-sm"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            style={{ fontSize: "0.82rem" }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Período</div>
          <div style={{ display: "flex", gap: 8 }}>
            <select
              className="form-select form-select-sm"
              value={periodoMes.getMonth()}
              onChange={(e) => setPeriodoMes(new Date(periodoMes.getFullYear(), Number(e.target.value), 1))}
              style={{ fontSize: "0.82rem", flex: 1 }}
            >
              {MESES_LABELS.map((label, i) => (
                <option key={i} value={i}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={periodoMes.getFullYear()}
              onChange={(e) => setPeriodoMes(new Date(Number(e.target.value), periodoMes.getMonth(), 1))}
              style={{ fontSize: "0.82rem", width: 100 }}
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>N° documento (opcional)</div>
          <input
            type="text"
            className="form-control form-control-sm"
            value={numeroDoc}
            onChange={(e) => setNumeroDoc(e.target.value)}
            style={{ fontSize: "0.82rem" }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Concepto</div>
          <input
            type="text"
            className="form-control form-control-sm"
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
            style={{ fontSize: "0.82rem" }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
            Monto {entry.moneda ? `(${entry.moneda})` : ""}
          </div>
          <input
            type="text"
            inputMode="decimal"
            className="form-control form-control-sm"
            placeholder="0.00"
            value={monto}
            onChange={(e) => {
              const normalized = e.target.value.replace(",", ".");
              if (normalized === "" || /^\d*\.?\d*$/.test(normalized)) {
                setMonto(normalized);
              }
            }}
            style={{ fontSize: "0.82rem" }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EstadoCuenta;
